import * as signalR from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import { API_ORIGIN } from '../config/endpoints.js';
import {
  ActionStatus,
  decodeGameActionResult,
  decodeGetBalanceResult,
  decodeStartGameActionResult,
  readForceDisconnectConnectionId,
} from './hubResultDecoder.js';

const HUB_URL = `${API_ORIGIN}/slots-mp`;
const EVENT_TIMEOUT_MS = 15000;
/** Health-check / reconnect probe (StartGameAction). */
const SESSION_PING_TIMEOUT_MS = 10000;

/** @returns {import('@microsoft/signalr').HubConnectionState | null} */
function hubState(connection) {
  return connection?.state ?? null;
}

export class ShiningCrownService {
  constructor() {
    this.connection = null;
    this._connected = false;
    this._connectPromise = null;
    this._connectionHandlers = null;
    this._connectionHandlersAttached = false;
    this._forceDisconnectAttached = false;
    /** While true, intentional stop/restart must not surface as connection-lost UI. */
    this._suppressConnectionEvents = false;
    /** Duplicate-session tab — block new hub invokes/reconnects. */
    this._sessionCeased = false;
  }

  /**
   * @param {{
   *   onConnected?: () => void,
   *   onDisconnected?: () => void,
   *   onReconnecting?: () => void,
   *   onForceDisconnect?: (payload?: unknown) => void,
   * } | null} handlers
   */
  setConnectionHandlers(handlers) {
    this._connectionHandlers = handlers;
    this._attachConnectionHandlers();
    this._attachForceDisconnectHandler();
  }

  _attachForceDisconnectHandler() {
    if (!this.connection || this._forceDisconnectAttached) return;
    this._forceDisconnectAttached = true;
    const onForceDisconnect = (payload) => {
      const id = readForceDisconnectConnectionId(payload);
      // Server includes the new connection; ignore when it is us.
      if (id && this.connection?.connectionId && id === this.connection.connectionId) {
        return;
      }
      this._connected = false;
      // Do not let withAutomaticReconnect reopen a handed-over session.
      this._sessionCeased = true;
      this._suppressConnectionEvents = true;
      void this.connection?.stop?.().finally(() => {
        this._suppressConnectionEvents = false;
      });
      this._connectionHandlers?.onForceDisconnect?.(payload);
    };
    const duplicateSessionEvents = [
      'ForceDisconnect',
      'forcedisconnect',
      'SessionEnded',
      'sessionended',
      'DuplicateSession',
      'duplicatesession',
      'SessionReplaced',
      'sessionreplaced',
    ];
    for (const name of duplicateSessionEvents) {
      this.connection.on(name, onForceDisconnect);
    }
  }

  _attachConnectionHandlers() {
    if (!this.connection || this._connectionHandlersAttached) return;
    this._connectionHandlersAttached = true;
    this.connection.onreconnecting(() => {
      this._connected = false;
      if (this._suppressConnectionEvents || this._sessionCeased) return;
      this._connectionHandlers?.onReconnecting?.();
    });
    this.connection.onreconnected(() => {
      if (this._sessionCeased) return;
      this._connected = true;
      this._connectionHandlers?.onConnected?.();
    });
    this.connection.onclose(() => {
      this._connected = false;
      if (this._suppressConnectionEvents || this._sessionCeased) return;
      this._connectionHandlers?.onDisconnected?.();
    });
  }

  async connect(accessToken) {
    if (this._sessionCeased) return;
    if (!this.connection) {
      const urlOptions = accessToken ? { accessTokenFactory: () => accessToken } : {};
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(HUB_URL, urlOptions)
        .withHubProtocol(new MessagePackHubProtocol())
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Warning)
        .build();
      this._attachConnectionHandlers();
      this._attachForceDisconnectHandler();
    }

    const state = this.connection.state;
    if (state === signalR.HubConnectionState.Connected) {
      this._connected = true;
      return;
    }
    if (state === signalR.HubConnectionState.Connecting || state === signalR.HubConnectionState.Reconnecting) {
      if (this._connectPromise) await this._connectPromise;
      return;
    }
    if (this._connectPromise) {
      await this._connectPromise;
      return;
    }

    this._connectPromise = this.connection
      .start()
      .then(() => {
        this._connected = true;
      })
      .finally(() => {
        this._connectPromise = null;
      });
    await this._connectPromise;
  }

  /**
   * Session bootstrap / reconnect — MessagePack contract uses StartGameAction only.
   * @param {{ isDemo?: boolean, partnerId?: string | number, gameName?: string, timeoutMs?: number }} [opts]
   */
  async startGameAction({
    isDemo,
    partnerId,
    gameName,
    timeoutMs,
  } = {}) {
    // MessagePack rejects string PartnerId (URL params are strings) — send a number.
    const partnerIdNum = Number(partnerId);
    if (!Number.isFinite(partnerIdNum)) {
      throw new Error(`Invalid PartnerId: ${partnerId}`);
    }
    const raw = await this._invokeWithEventFallback(
      'StartGameAction',
      'StartGameActionResult',
      {
        PartnerId: partnerIdNum,
        GameName: gameName.trim(),
        IsDemo: Boolean(isDemo),
      },
      timeoutMs ?? EVENT_TIMEOUT_MS,
    );
    const decoded = decodeStartGameActionResult(raw);
    if (decoded.actionStatus && decoded.actionStatus !== ActionStatus.Success) {
      const err = new Error(`StartGameAction failed: ${decoded.actionStatus}`);
      err.actionStatus = decoded.actionStatus;
      throw err;
    }
    return decoded;
  }

  /** @deprecated Prefer {@link startGameAction} — kept for call-site compatibility. */
  async getInitialState(opts) {
    return this.startGameAction(opts);
  }

  /** Live session check — same hub call as bootstrap (returns balance + board). */
  async pingSession({ isDemo, partnerId, gameName } = {}) {
    return this.startGameAction({
      isDemo,
      partnerId,
      gameName,
      timeoutMs: SESSION_PING_TIMEOUT_MS,
    });
  }

  async getBalance() {
    const raw = await this._invokeWithEventFallback(
      'GetBalance',
      'GetBalanceResult',
      undefined,
    );
    return decodeGetBalanceResult(raw);
  }

  async spin(amount) {
    const amountStr = String(amount);
    const raw = await this._invokeWithEventFallback(
      'GameAction',
      'GameActionResult',
      {
        ActionType: 'Spin',
        Model: { Amount: amountStr, SpinData: {} },
      },
    );
    const decoded = decodeGameActionResult(raw);
    const status = decoded.actionStatus;
    if (status && status !== ActionStatus.Success) {
      const err = new Error(`GameAction failed: ${status}`);
      err.actionStatus = status;
      err.actionType = decoded.actionType;
      throw err;
    }
    // Guard unknown action types on refusal payloads that still claim success.
    if (decoded.actionType && decoded.actionType !== 'Spin') {
      return decoded;
    }
    return decoded;
  }

  async disconnect() {
    if (!this.connection) return;
    this._suppressConnectionEvents = true;
    try {
      await this.connection.stop();
    } catch (_) {
      /* ignore */
    } finally {
      this._suppressConnectionEvents = false;
    }
    this._connected = false;
  }

  /** Stop hub and automatic reconnect (kicked / duplicate session tab). */
  async ceaseSession() {
    this._sessionCeased = true;
    return this.disconnect();
  }

  /** Stop and start the hub (iOS resume / stale WebSocket). */
  async restartConnection() {
    if (this._sessionCeased) return;
    if (!this.connection) return;
    const state = hubState(this.connection);
    if (
      state === signalR.HubConnectionState.Connecting ||
      state === signalR.HubConnectionState.Reconnecting
    ) {
      if (this._connectPromise) await this._connectPromise;
      return;
    }
    this._suppressConnectionEvents = true;
    try {
      if (state === signalR.HubConnectionState.Connected) {
        try {
          await this.connection.stop();
        } catch (_) {
          /* ignore */
        }
      }
      this._connected = false;
      await this.connection.start();
      this._connected = true;
    } finally {
      this._suppressConnectionEvents = false;
    }
  }

  /** Live hub socket is up (not merely mid-reconnect). */
  isConnected() {
    const connected =
      hubState(this.connection) === signalR.HubConnectionState.Connected;
    if (connected) this._connected = true;
    return connected;
  }

  /** SignalR automatic reconnect in progress. */
  isReconnecting() {
    const s = hubState(this.connection);
    return (
      s === signalR.HubConnectionState.Connecting ||
      s === signalR.HubConnectionState.Reconnecting
    );
  }

  async _invokeWithEventFallback(method, eventName, payload, timeoutMs = EVENT_TIMEOUT_MS) {
    if (this._sessionCeased) {
      return Promise.reject(new Error('Session ceased'));
    }
    await this.connect();
    if (this._sessionCeased) {
      return Promise.reject(new Error('Session ceased'));
    }
    return new Promise((resolve, reject) => {
      let timer = null;
      let settled = false;
      const finish = (cb, value) => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        this.connection.off(eventName, listener);
        if (
          cb === resolve &&
          hubState(this.connection) === signalR.HubConnectionState.Connected
        ) {
          this._connected = true;
        }
        cb(value);
      };
      const listener = (value) => finish(resolve, value);
      this.connection.on(eventName, listener);
      const invokePromise =
        payload === undefined
          ? this.connection.invoke(method)
          : this.connection.invoke(method, payload);
      invokePromise
        .then((direct) => {
          // MessagePack: invoke may resolve with the result (including refusals).
          if (direct != null) finish(resolve, direct);
        })
        .catch((err) => finish(reject, err));
      timer = setTimeout(
        () => finish(reject, new Error(`${eventName} timeout`)),
        timeoutMs,
      );
    });
  }
}
